import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { NOT_FOUND_SEO, SITE_URL, getPageSeo, isNoindexPath, OG_IMAGE } from './seoConfig.mjs';

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const removeMeta = (attr, key) => {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (el) el.remove();
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const JSON_LD_ID = 'seo-json-ld';

const applyJsonLd = (blocks) => {
  document.querySelectorAll(`script[data-seo="${JSON_LD_ID}"]`).forEach((el) => el.remove());
  blocks.forEach((block) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.seo = JSON_LD_ID;
    script.textContent = JSON.stringify(block);
    document.head.appendChild(script);
  });
};

/**
 * Applies the page's search + social metadata in the browser.
 *
 * The build-time prerender writes the same tags into static HTML, so this is
 * what keeps client-side navigation (and the preview environment) correct.
 */
export const usePageSeo = (override) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = override || getPageSeo(pathname);
    const noindex = Boolean(override?.noindex) || isNoindexPath(pathname);

    if (!seo) {
      // Portal routes: no public metadata, but they must stay out of the index.
      if (noindex) upsertMeta('name', 'robots', 'noindex, nofollow');
      return;
    }

    const canonical = `${SITE_URL}${seo.path === '/' ? '/' : seo.path}`;
    const image = `${SITE_URL}${seo.ogImage || OG_IMAGE}`;

    document.title = seo.title;
    upsertMeta('name', 'description', seo.description);
    upsertCanonical(canonical);

    if (noindex) {
      upsertMeta('name', 'robots', 'noindex, nofollow');
    } else {
      upsertMeta('name', 'robots', 'index, follow, max-image-preview:large');
    }

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'MAD Donuts');
    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);
    upsertMeta('name', 'twitter:image', image);

    const verification = process.env.REACT_APP_GSC_VERIFICATION;
    if (verification) {
      upsertMeta('name', 'google-site-verification', verification);
    } else {
      removeMeta('name', 'google-site-verification');
    }

    applyJsonLd(seo.jsonLd ? seo.jsonLd() : []);
  }, [pathname, override]);
};

/** Drop-in for pages that are not in the public page table (e.g. the 404). */
export const NotFoundSeo = () => {
  usePageSeo(NOT_FOUND_SEO);
  return null;
};

/**
 * App-level guard: the portal routes render outside PublicLayout, so they never
 * mount <Seo>. They still must carry a noindex tag of their own — robots.txt
 * alone does not stop an already-known URL being indexed.
 */
export const PortalNoindex = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (isNoindexPath(pathname)) upsertMeta('name', 'robots', 'noindex, nofollow');
  }, [pathname]);

  return null;
};

const Seo = () => {
  usePageSeo();
  return null;
};

export default Seo;
